/**
 * Patient Document Upload Component
 * Comprehensive document management for patient onboarding
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Alert,
  LinearProgress,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Delete,
  Visibility,
  CheckCircle,
  Warning,
  Add,
  FilePresent
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// API Service
import { documentsApi } from '../../services/api/patientComprehensive';

const PatientDocumentUpload = ({ onSaved, onSkip, initialData }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    documentType: '',
    visibility: 'patient_doctor'
  });
  const [errors, setErrors] = useState({});

  // Document types
  const documentTypes = [
    { value: 'medical_report', label: 'Medical Report' },
    { value: 'lab_result', label: 'Laboratory Result' },
    { value: 'imaging', label: 'Imaging Report' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'discharge_summary', label: 'Discharge Summary' },
    { value: 'surgery_report', label: 'Surgery Report' },
    { value: 'pathology_report', label: 'Pathology Report' },
    { value: 'psychiatric_evaluation', label: 'Psychiatric Evaluation' },
    { value: 'psychological_assessment', label: 'Psychological Assessment' },
    { value: 'therapy_notes', label: 'Therapy Notes' },
    { value: 'insurance_card', label: 'Insurance Card' },
    { value: 'id_proof', label: 'ID Proof' },
    { value: 'other', label: 'Other' }
  ];

  const visibilityOptions = [
    { value: 'patient_only', label: 'Patient Only' },
    { value: 'patient_doctor', label: 'Patient & Doctor' },
    { value: 'patient_doctor_receptionist', label: 'Patient, Doctor & Receptionist' },
    { value: 'all_staff', label: 'All Staff' }
  ];

  // Load existing documents on mount
  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentsApi.getDocuments();
      setDocuments(response.data || []);
    } catch (err) {
      console.error('Error loading documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/gif',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload PDF, image, or Word document.');
        return;
      }

      setSelectedFile(file);
      // Auto-fill title if not provided
      if (!uploadForm.title) {
        setUploadForm(prev => ({
          ...prev,
          title: file.name.replace(/\.[^/.]+$/, '') // Remove file extension
        }));
      }
    }
  };

  const validateUploadForm = () => {
    const newErrors = {};
    
    if (!uploadForm.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!uploadForm.documentType) {
      newErrors.documentType = 'Document type is required';
    }
    
    if (!selectedFile) {
      newErrors.file = 'Please select a file';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpload = async () => {
    if (!validateUploadForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('document', selectedFile);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('document_type', uploadForm.documentType);
      formData.append('visibility', uploadForm.visibility);
      
      await documentsApi.uploadDocument(formData);
      
      toast.success('Document uploaded successfully!');
      setShowUploadDialog(false);
      setSelectedFile(null);
      setUploadForm({
        title: '',
        description: '',
        documentType: '',
        visibility: 'patient_doctor'
      });
      
      // Reload documents
      await loadDocuments();
      
      // Redirect to profile page after successful upload
      window.location.href = '/patient-dashboard/profile';
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentsApi.deleteDocument(documentId);
        toast.success('Document deleted successfully!');
        await loadDocuments();
      } catch (err) {
        console.error('Error deleting document:', err);
        toast.error('Failed to delete document');
      }
    }
  };

  const getDocumentTypeLabel = (type) => {
    const docType = documentTypes.find(dt => dt.value === type);
    return docType ? docType.label : type;
  };

  const getVisibilityLabel = (visibility) => {
    const visOption = visibilityOptions.find(vo => vo.value === visibility);
    return visOption ? visOption.label : visibility;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSkip = () => {
    // Redirect to profile page
    window.location.href = '/patient-dashboard/profile';
  };

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading documents...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth={800} mx="auto">
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
            Document Management
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload your medical documents, reports, and other relevant files. This helps healthcare providers understand your medical history better.
          </Typography>

          {/* Upload Button */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setShowUploadDialog(true)}
              sx={{ mb: 2 }}
            >
              Upload New Document
            </Button>
            
            <Typography variant="body2" color="text.secondary">
              Supported formats: PDF, JPG, PNG, GIF, DOC, DOCX (Max size: 10MB)
            </Typography>
          </Box>

          {/* Documents List */}
          {documents.length > 0 ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Your Documents ({documents.length})
              </Typography>
              
              <List>
                {documents.map((doc) => (
                  <ListItem key={doc.id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FilePresent color="primary" />
                          <Typography variant="subtitle1">
                            {doc.title}
                          </Typography>
                          {doc.is_verified && (
                            <CheckCircle color="success" fontSize="small" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {getDocumentTypeLabel(doc.document_type)} • {getVisibilityLabel(doc.visibility)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {doc.description && `${doc.description} • `}
                            {formatFileSize(doc.file_size)} • Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => window.open(doc.document, '_blank')}
                          title="View document"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDocument(doc.id)}
                          title="Delete document"
                        >
                          <Delete />
                        </IconButton>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              No documents uploaded yet. Click "Upload New Document" to get started.
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleSkip}
            >
              Skip for Now
            </Button>
            <Button
              variant="contained"
              onClick={() => window.location.href = '/patient-dashboard/profile'}
            >
              Continue to Profile
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Document
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <input
                accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                style={{ display: 'none' }}
                id="document-file"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="document-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {selectedFile ? selectedFile.name : 'Choose File'}
                </Button>
              </label>
              {errors.file && (
                <Typography color="error" variant="caption">
                  {errors.file}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Document Title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
                error={!!errors.title}
                helperText={errors.title}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                multiline
                rows={3}
                value={uploadForm.description}
                onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.documentType}>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={uploadForm.documentType}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, documentType: e.target.value }))}
                  label="Document Type"
                >
                  {documentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.documentType && (
                  <FormHelperText>{errors.documentType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={uploadForm.visibility}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, visibility: e.target.value }))}
                  label="Visibility"
                >
                  {visibilityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientDocumentUpload;
